import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft } from 'lucide-react'
import { WorkflowInputSpec } from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Text } from '@/components/ui/Text'
import { CompareSegmentsGroup } from '@/components/quick-study-results/CompareSegmentsPicker'
import { AudienceSummary } from '@/lib/api'
import { AudienceSelector } from '@/pages/ConversationsPage/ComposeModal/AudienceSelector'

import { TwinTestCutFilterTrigger } from './TwinTestCutPicker/TwinTestCutFilterTrigger'
import { TwinTestCutPicker } from './TwinTestCutPicker/TwinTestCutPicker'
import {
  TWIN_TEST_SEGMENT_BY_INPUT_KEY,
  activeCutLabel,
  segmentByFromSelectedCuts,
} from './TwinTestCutPicker/twinTestCutMapping'
import {
  ThreadStimulusInput,
  hasBoundStimulusValue,
} from './ThreadStimulusInput'

/**
 * The fields a thread command declares, rendered from the template rather than
 * from a picker keyed to a slug the frontend recognises.
 *
 * WRITTEN FRESH rather than lifted out of `WorkflowsPage/WorkflowDetailPage`,
 * whose `renderInputField` does the same job and is the right long-term home
 * for one shared component (plan D3). That file is being edited on this branch
 * by another session, so duplicating the three cases here now and reconciling
 * once is cheaper than a merge conflict in a file this change does not own.
 *
 * Deliberately CONTROLLED and stateless: the confirm screen owns the values,
 * because it is also the thing that decides whether the run may start and that
 * seeds a value from a command the reader typed.
 *
 * Narrower than the workflow page's version on purpose. There is no per-field
 * "Required" error here: the panel is 280px wide under a composer, the confirm
 * button is simply disabled until every required field has a value, and a
 * column of red text in that space would be louder than the decision it
 * guards.
 *
 * The remember-audience checkbox (arms cards only) is also controlled: this
 * component never writes to storage. The page decides when to persist,
 * typically only on confirm.
 */
export interface ThreadCommandInputsProps {
  inputs: Array<WorkflowInputSpec>
  /** Current values, keyed by `WorkflowInputSpec.key`. `null` means unanswered. */
  values: Record<string, string | null>
  onChange: (key: string, value: string | null) => void
  /** Ready audiences for any `audience` input. Empty until one is declared. */
  audiences: Array<AudienceSummary>
  audiencesLoading: boolean
  /** True while a run is starting, or while the thread cannot accept one. */
  disabled: boolean
  /**
   * When true (an `arms` command), omit the composer-owned text input from the
   * card. That field is answered in the composer after arming, not here.
   */
  hideComposerOwnedText?: boolean
  /**
   * When true, omit the audience field's label and picker from the card. Set
   * by the owner (D4, plan `2026-09-15-twin-test-question-shape-and-no-
   * command-path-plan.md` Step 3) while it renders its own resolved-audience
   * summary line above this component instead: the seed's audience value
   * already names a loaded audience option, so re-asking "Which audience
   * should answer?" would be redundant. The value stays in `values` either
   * way — this only affects what renders.
   */
  hideAudienceField?: boolean
  /**
   * When true, show the "Remember this audience" checkbox.
   * Arms confirm cards only — never `/report` or `/slack`.
   */
  showRememberAudience?: boolean
  /** Controlled remember-audience value. Default off at the owner. */
  rememberAudience?: boolean
  onRememberAudienceChange?: (remember: boolean) => void
  /**
   * Demographic cut groups for the optional `segment_by` input. Empty hides
   * the picker (the key still travels on the run-input bag when set).
   */
  cutGroups?: Array<CompareSegmentsGroup>
  /** Confirm card hides Continue while the cut picker screen is open. */
  onFilterStepChange?: (isOpen: boolean) => void
}

/**
 * Key of the text input the composer owns once a command arms — the template's
 * first `text` input. Same derivation as `askInputKey` on the page; carries no
 * template vocabulary. Undefined when the template declares no text input.
 */
export function composerOwnedTextInputKey(
  inputs: Array<WorkflowInputSpec>,
): string | undefined {
  return inputs.find((spec) => spec.type === 'text')?.key
}

/**
 * Hide the first text field on the confirm card when it is optional (`ask`
 * on `/test`). The composer owns that field after Confirm arms; a blank ask
 * must not reappear as “What do you want to test?”. Seeded rest still lives
 * in `values` and is dropped into the composer for edit-then-send.
 */
export function hideFirstOptionalTextOnCard(
  inputs: Array<WorkflowInputSpec>,
): boolean {
  const first = inputs.find((spec) => spec.type === 'text')
  return Boolean(first && first.required === false)
}

/**
 * Inputs the confirm card collects. ONE filter for both the render path and
 * `hasEveryRequiredInput`: when the command arms, the first text input is the
 * composer's, so it must not appear on the card and must not gate confirm.
 */
export function cardInputs(
  inputs: Array<WorkflowInputSpec>,
  options: { hideComposerOwnedText: boolean },
): Array<WorkflowInputSpec> {
  if (!options.hideComposerOwnedText) {
    return inputs
  }
  const ownedKey = composerOwnedTextInputKey(inputs)
  if (!ownedKey) {
    return inputs
  }
  return inputs.filter((spec) => spec.key !== ownedKey)
}

/**
 * Whether every `required` input has an answer, which is the whole of the
 * confirm button's gate.
 *
 * Blank-but-present text counts as unanswered: a run started on a whitespace
 * string satisfies the client and then fails whatever step reads it.
 *
 * Pass `hideComposerOwnedText` for an arms command so the composer-owned field
 * is skipped — otherwise a required first text input would deadlock confirm
 * after that field is hidden from the card.
 */
export function hasEveryRequiredInput(
  inputs: Array<WorkflowInputSpec>,
  values: Record<string, string | null>,
  options: { hideComposerOwnedText?: boolean } = {},
): boolean {
  return cardInputs(inputs, {
    hideComposerOwnedText: options.hideComposerOwnedText ?? false,
  }).every((spec) => {
    if (!spec.required) {
      return true
    }
    return (values[spec.key] ?? '').trim().length > 0
  })
}

/**
 * The additional run gate for an optional stimulus input.
 *
 * A media-referencing ask needs one hosted ref already on the input JSON
 * (registry-seeded or composer-bound — `hasBoundStimulusValue` does not
 * distinguish). The reader may explicitly acknowledge the fallback, in which
 * case the same null input is retained and the UI labels the run as a
 * concept-level test.
 */
export function hasStimulusGateSatisfied(
  inputs: Array<WorkflowInputSpec>,
  values: Record<string, string | null>,
  proceedWithoutStimulus: boolean = false,
  referencesStimulus: boolean = false,
): boolean {
  const stimulusInputs = inputs.filter((spec) => spec.type === 'stimulus')
  if (stimulusInputs.length === 0) {
    return true
  }
  if (!referencesStimulus) {
    return true
  }
  if (
    stimulusInputs.some((spec) => hasBoundStimulusValue(values[spec.key]))
  ) {
    return true
  }
  return proceedWithoutStimulus
}

export function ThreadCommandInputs(props: ThreadCommandInputsProps) {
  const {
    inputs,
    values,
    onChange,
    audiences,
    audiencesLoading,
    disabled,
    hideComposerOwnedText = false,
    hideAudienceField = false,
    showRememberAudience = false,
    rememberAudience = false,
    onRememberAudienceChange,
    cutGroups = [],
    onFilterStepChange,
  } = props

  const { t } = useTranslation('app')
  const { t: tCommon } = useTranslation('common')

  const [isFilterStep, setIsFilterStep] = useState(false)

  const visibleInputs = cardInputs(inputs, { hideComposerOwnedText }).filter(
    (spec) => {
      if (spec.key === TWIN_TEST_SEGMENT_BY_INPUT_KEY) {
        return cutGroups.some((group) => group.options.length > 0)
      }
      return (
        spec.type !== 'stimulus' || hasBoundStimulusValue(values[spec.key])
      )
    },
  )
  const segmentBySpec = visibleInputs.find(
    (spec) => spec.key === TWIN_TEST_SEGMENT_BY_INPUT_KEY,
  )
  const fieldInputs = visibleInputs.filter(
    (spec) => spec.key !== TWIN_TEST_SEGMENT_BY_INPUT_KEY,
  )
  /**
   * What actually renders below. `fieldInputs` itself stays the full set, so
   * `audienceSpec` / `canOpenFilters` below still see the audience field even
   * while it is hidden from view.
   */
  const renderedFieldInputs = hideAudienceField
    ? fieldInputs.filter((spec) => spec.type !== 'audience')
    : fieldInputs
  const audienceSpec = fieldInputs.find((spec) => spec.type === 'audience')
  const isAudienceSelected = audienceSpec
    ? (values[audienceSpec.key] ?? '').trim().length > 0
    : true
  const canOpenFilters = Boolean(segmentBySpec) && isAudienceSelected
  const segmentByValue = segmentBySpec
    ? values[segmentBySpec.key]
    : null
  const selectedCuts = segmentBySpec
    ? cutGroups.flatMap((group) =>
        group.options
          .filter((option) => option.id.startsWith(`${segmentByValue ?? ''}::`))
          .map((option) => option.id),
      )
    : []
  const filterActiveLabel = activeCutLabel(cutGroups, segmentByValue)

  useEffect(() => {
    if (!canOpenFilters && isFilterStep) {
      setIsFilterStep(false)
    }
  }, [canOpenFilters, isFilterStep])

  useEffect(() => {
    onFilterStepChange?.(isFilterStep)
  }, [isFilterStep, onFilterStepChange])

  if (visibleInputs.length === 0 && !showRememberAudience) {
    return null
  }

  const rememberAudienceCheckbox = showRememberAudience ? (
    <label
      htmlFor="thread-command-remember-audience"
      className="flex cursor-pointer items-start gap-2"
    >
      <Checkbox
        id="thread-command-remember-audience"
        checked={rememberAudience}
        onCheckedChange={(checked) => {
          onRememberAudienceChange?.(checked === true)
        }}
        disabled={disabled}
        data-testid="thread-command-remember-audience"
      />
      <Text size="sm" as="span" className="leading-snug">
        {t('threads.detail.command.rememberSession')}
      </Text>
    </label>
  ) : null

  if (isFilterStep && segmentBySpec) {
    return (
      <div className="flex flex-col gap-2" data-testid="thread-command-inputs">
        <button
          type="button"
          onClick={() => setIsFilterStep(false)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          data-testid="thread-command-filters-back"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden="true" />
          {t('audiences.filters')}
        </button>
        <div className="max-h-64 overflow-y-auto">
          <TwinTestCutPicker
            groups={cutGroups}
            selected={selectedCuts}
            onChange={(cuts) =>
              onChange(segmentBySpec.key, segmentByFromSelectedCuts(cuts))
            }
          />
        </div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          className="text-sm w-full"
          data-testid="thread-command-filters-done"
          onClick={() => setIsFilterStep(false)}
        >
          {tCommon('done')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2" data-testid="thread-command-inputs">
      {renderedFieldInputs.map((spec) => {
        const value = values[spec.key] ?? null
        const testId = `thread-command-input-${spec.key}`

        return (
          <div className="flex flex-col gap-1.5" key={spec.key}>
            <Text size="sm" weight="medium" as="label">
              {spec.label}
            </Text>
            {spec.type === 'stimulus' ? (
              <ThreadStimulusInput
                inputKey={spec.key}
                value={value}
                onChange={(nextValue) => onChange(spec.key, nextValue)}
                disabled={disabled}
              />
            ) : spec.type === 'audience' ? (
              <div data-testid={testId}>
                <AudienceSelector
                  audiences={audiences}
                  selectedId={value}
                  onSelect={(id) => onChange(spec.key, id)}
                  isLoading={audiencesLoading}
                />
              </div>
            ) : spec.type === 'select' ? (
              <Select
                value={value ?? undefined}
                onValueChange={(next) => onChange(spec.key, next)}
                disabled={disabled}
              >
                <SelectTrigger
                  data-testid={testId}
                  className="data-[placeholder]:text-muted-foreground"
                >
                  {/* Self-closing: Radix renders the chosen item's label
                      itself, and the placeholder while nothing is chosen. */}
                  <SelectValue placeholder={t('threads.detail.command.select')} />
                </SelectTrigger>
                <SelectContent>
                  {(spec.options ?? []).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={value ?? ''}
                onChange={(event) => onChange(spec.key, event.target.value)}
                disabled={disabled}
                placeholder={spec.placeholder}
                data-testid={testId}
              />
            )}
          </div>
        )
      })}
      {canOpenFilters ? (
        <TwinTestCutFilterTrigger
          activeLabel={filterActiveLabel}
          disabled={disabled}
          onClick={() => setIsFilterStep(true)}
        />
      ) : null}
      {rememberAudienceCheckbox}
    </div>
  )
}
